import React, { useState, useEffect } from 'react'
import { Card, Col, Row, Spinner, Button, Modal } from 'react-bootstrap'
import { FaExclamationTriangle } from 'react-icons/fa'
import {
  setSelectedModelId,
  selectSelectedModelId,
  setIsComparing,
} from '../../store/slices/modelSlice'
import {
  useDeleteModelMutation,
  useFetchModelsQuery,
  useStopTrainModelMutation,
  useFineTuneModelMutation,
  useGetModelQuery,
  useCreateModelMutation,
} from '../../services/modelsApi'
import { useGetProjectQuery } from '../../services/projectsApi'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { selectSelectedProjectId, setActiveModelSubView } from '../../store/slices/projectSlice'
import styled from 'styled-components'
import ConfirmDialog from '../modals/ConfirmDialog'
import ErrorDialog from '../modals/ErrorDialog'
import { adjectives, nouns } from '../../utils/modelNameWords'
import NoModelsCard from './ModelsOverview/components/NoModelsCard'
import NewModelModal from '../modals/NewModelModal'
import { useNavigate } from 'react-router-dom'
import ModelTrainingModal from '../modals/ModelTrainingModal'
import { useDatasetStats } from '../../utils/datasetStats'
import { Model } from '../../types/models/model'
import ModelPerformanceCard from '../cards/ModelPerformanceCard/ModelPerformanceCard'
import ModelHeader from './ModelHeader'

const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
`

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #1a1a1a;
  min-height: 0;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 24px;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 8px 16px 16px;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`
interface BaseModel {
  id: string
  name: string
}

const ModelView: React.FC = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showStopTrainingDialog, setShowStopTrainingDialog] = useState(false)
  const [showNewModelModal, setShowNewModelModal] = useState(false)
  const [selectedBaseModel, setSelectedBaseModel] = useState<BaseModel | null>(null)
  const [filterText, setFilterText] = useState('')
  const [showTrainingModal, setShowTrainingModal] = useState(false)
  const [fineTuneTrainingSources, setFineTuneTrainingSources] = useState<string[]>([])
  const [fineTuneValidationSources, setFineTuneValidationSources] = useState<string[]>([])
  const [fineTuneTagIds, setFineTuneTagIds] = useState<string[]>([])

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const { data: modelsData, error } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )
  const {
    data: selectedModel,
    isLoading: isLoadingModel,
    isFetching: isFetchingModel,
  } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })
  const [deleteModel] = useDeleteModelMutation()
  const [stopTrainModel] = useStopTrainModelMutation()
  const [fineTuneModel, { isLoading: isRetraining }] = useFineTuneModelMutation()
  const [createModel] = useCreateModelMutation()

  const showModelGenerationView = ['unknown', 'initialized', 'stopping', 'err'].includes(
    selectedModel?.state.toLowerCase() || ''
  )
  const showModelTrainingView = ['training', 'initialized'].includes(
    selectedModel?.state.toLowerCase() || ''
  )

  const trainingStatsMap = useDatasetStats(
    selectedProjectId || '',
    project?.datasetid || '',
    fineTuneTrainingSources || []
  )
  const validationStatsMap = useDatasetStats(
    selectedProjectId || '',
    project?.datasetid || '',
    fineTuneValidationSources || []
  )

  const trainingAggregate = trainingStatsMap.__aggregate__
  const validationAggregate = validationStatsMap.__aggregate__
  const totalTrainingImages = trainingAggregate?.total_annotated_images || 0
  const totalTrainingAnnotations = Object.values(
    trainingAggregate?.annotations_per_class || {}
  ).reduce((sum, classData: any) => sum + (classData.count || 0), 0)
  const totalValidationImages = validationAggregate?.total_annotated_images || 0
  const totalValidationAnnotations = Object.values(
    validationAggregate?.annotations_per_class || {}
  ).reduce((sum, classData: any) => sum + (classData.count || 0), 0)

  const generateRandomName = () => {
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)
    return `${capitalize(randomAdjective)} ${capitalize(randomNoun)}`
  }

  const generateFineTuneName = () => {
    if (!selectedModel?.name) return ''
    const randomChars = Math.random().toString(36).substring(2, 4).toUpperCase()
    return `${selectedModel.name}-${randomChars}`
  }

  const handleDeleteModel = async () => {
    if (!selectedModelId) return
    try {
      // Delete the model and wait for completion
      await deleteModel({ id: selectedModelId }).unwrap()
    } catch (err) {
      setErrorMessage(
        err
          ? (err as any).data?.message || 'Failed to delete model'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
    setShowDeleteConfirm(false)
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
  }

  const handleStopTraining = async () => {
    if (selectedModelId) {
      try {
        const stoppedModel = await stopTrainModel({
          id: selectedModelId,
        }).unwrap()
        dispatch(
          setSelectedModelId({
            modelId: stoppedModel.id,
            projectId: selectedProjectId || '',
          })
        )
        setShowStopTrainingDialog(false)
      } catch (err) {
        setErrorMessage(
          err
            ? (err as any).data?.message || 'Failed to stop training'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
  }

  const handleFineTuneModel = async () => {
    if (!selectedModelId || !selectedModel) {
      setErrorMessage('No model selected')
      setShowError(true)
      return
    }

    try {
      // Get original dataset sources from localStorage
      const originalDatasetSources = JSON.parse(localStorage.getItem(selectedModelId) || '[]')

      // Combine original and current sources, removing duplicates
      const combinedTrainingSources = Array.from(
        new Set([...(originalDatasetSources?.datasource || []), ...(fineTuneTrainingSources || [])])
      )

      const combinedValidationSources = Array.from(
        new Set([
          ...(originalDatasetSources?.validation_datasource || []),
          ...(fineTuneValidationSources || []),
        ])
      )

      const updatedModel = await fineTuneModel({
        id: selectedModel.id,
        name: generateFineTuneName(),
        epochs: 5,
        freeze: 10,
        train_metadata_filter: { datasource: combinedTrainingSources },
        validation_metadata_filter: { datasource: combinedValidationSources },
      }).unwrap()
      dispatch(
        setSelectedModelId({
          modelId: updatedModel.id,
          projectId: selectedProjectId || '',
        })
      )
      dispatch(setActiveModelSubView('Training'))
      setShowTrainingModal(false)
    } catch (err) {
      setErrorMessage(
        err
          ? (err as any).data?.message || 'Failed to update model'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const handleCreateModel = () => {
    setShowNewModelModal(true)
  }

  const handleGenerateNewModel = async () => {
    if (selectedBaseModel && selectedProjectId) {
      try {
        const newModelName = generateRandomName()
        const newModel = await createModel({
          project_id: selectedProjectId,
          name: newModelName,
          description: '',
        }).unwrap()

        // Wait for a brief moment to allow the cache to update
        await new Promise((resolve) => setTimeout(resolve, 100))

        // Set the selected model ID
        dispatch(
          setSelectedModelId({
            modelId: newModel.id,
            projectId: selectedProjectId,
          })
        )

        // Navigate to the training subview
        navigate(`/models/${selectedProjectId}/training`)

        // Close the modal
        setShowNewModelModal(false)
        setSelectedBaseModel(null)
        setFilterText('')
      } catch (err: any) {
        const message = err?.data?.message || 'Failed to create new model'
        setErrorMessage(message)
        setShowError(true)
      }
    }
  }

  useEffect(() => {
    dispatch(setIsComparing(false))
  }, [dispatch])

  useEffect(() => {
    if (selectedModel?.state === 'TRAINING' || selectedModel?.state === 'INITIALIZED') {
      dispatch(setActiveModelSubView('Training'))
      navigate(`/models/${selectedProjectId}/training`)
    }
  }, [selectedModel?.state, selectedProjectId, navigate, dispatch])

  return (
    <ViewContainer>
      {isLoadingModel || isFetchingModel ? (
        <Card className="model-view">
          <Card.Body className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" />
          </Card.Body>
        </Card>
      ) : error ? (
        <Card className="model-view">
          <Card.Body>
            <p className="text-danger">
              {'status' in error
                ? `Error ${error.status}: ${JSON.stringify(error.data)}`
                : error
                ? (error as any).data?.message
                : 'Unknown Error'}
            </p>
          </Card.Body>
        </Card>
      ) : !modelsData?.models || modelsData.models.length === 0 ? (
        <>
          <NoModelsCard onCreateModel={handleCreateModel} />
          <NewModelModal
            show={showNewModelModal}
            onClose={() => {
              setShowNewModelModal(false)
              setSelectedBaseModel(null)
              setFilterText('')
            }}
            baseModels={[
              {
                id: 'baseModel1',
                name: 'AG Base',
                image: '/agileview-eye.png',
              },
            ]}
            filterText={filterText}
            setFilterText={setFilterText}
            selectedBaseModel={selectedBaseModel}
            onBaseModelSelect={setSelectedBaseModel}
            onGenerateNewModel={handleGenerateNewModel}
          />
          <ErrorDialog
            show={!!errorMessage}
            onHide={() => setErrorMessage(null)}
            message={errorMessage || ''}
          />
        </>
      ) : !selectedModel ? (
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card
              className="text-center shadow"
              style={{
                margin: '100px auto',
                padding: '20px',
                border: 'none',
                borderRadius: '10px',
                backgroundColor: '#2a2a2a',
              }}
            >
              <Card.Body>
                <FaExclamationTriangle size={60} color="red" className="mb-4" />
                <Card.Title
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#ffffff',
                  }}
                >
                  No Model Selected
                </Card.Title>
                <Card.Text style={{ fontSize: '16px', color: '#6c757d' }}>
                  Select a model, or you can generate a new model by selecting &quot;New Model&quot;
                  on the left hand side.
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <DashboardContainer>
          <ScrollableContent>
            <Row>
              <Col>
                <ModelHeader showParameters={false} />
              </Col>
            </Row>

            {!showModelGenerationView && !showModelTrainingView && <ModelPerformanceCard />}

            {/* Delete Confirmation Modal */}
            <Modal
              show={showDeleteModal}
              onHide={handleDeleteCancel}
              centered
              className="dark-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Delete</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to delete the model &quot;
                {selectedModel?.name}&quot;? This action cannot be undone.
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleDeleteCancel}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleDeleteModel}>
                  Delete Model
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Stop Training Confirmation Modal */}
            <Modal
              show={showStopTrainingDialog}
              onHide={() => setShowStopTrainingDialog(false)}
              centered
              className="dark-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>Stop Training</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to stop the model training? This action cannot be undone.
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowStopTrainingDialog(false)}
                >
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleStopTraining}>
                  Stop Training
                </Button>
              </Modal.Footer>
            </Modal>

            <ConfirmDialog
              show={showDeleteConfirm}
              onHide={() => setShowDeleteConfirm(false)}
              onConfirmRight={handleDeleteModel}
              title="Delete Model"
              message={`Are you sure you want to delete model "${selectedModel?.name}"? This action cannot be undone.`}
              confirmRightText="Delete"
              cancelText="Cancel"
              variant="danger"
            />
          </ScrollableContent>
        </DashboardContainer>
      )}

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />

      {selectedModel && (
        <ModelTrainingModal
          show={showTrainingModal}
          onHide={() => setShowTrainingModal(false)}
          model={selectedModel as Model}
          project={project}
          isRetraining={isRetraining}
          selectedTrainingSources={fineTuneTrainingSources}
          selectedValidationSources={fineTuneValidationSources}
          selectedTagIds={fineTuneTagIds}
          totalTrainingImages={totalTrainingImages}
          totalTrainingAnnotations={totalTrainingAnnotations}
          totalValidationImages={totalValidationImages}
          totalValidationAnnotations={totalValidationAnnotations}
          onTrainingSourcesChange={setFineTuneTrainingSources}
          onValidationSourcesChange={setFineTuneValidationSources}
          onTagSelectionChange={setFineTuneTagIds}
          onRetrain={handleFineTuneModel}
        />
      )}
    </ViewContainer>
  )
}

export default ModelView
